import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

const ConfirmContext = createContext(null);

/**
 * An in-app replacement for window.confirm.
 *
 * The browser's own dialog announces the server's address, can't be styled, and
 * looks alarming in a way that has nothing to do with what's being asked. This
 * one reads like the rest of the app and can distinguish "delete forever" from
 * "are you sure".
 *
 * Used the same way, so call sites barely change:
 *   if (!(await confirm({ title: "Delete this?" }))) return;
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((options) => {
    setState({
      title: "Are you sure?",
      body: "",
      confirmText: "Confirm",
      cancelText: "Cancel",
      destructive: false,
      ...(typeof options === "string" ? { title: options } : options),
    });
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (answer) => {
    setState(null);
    resolver.current?.(answer);
    resolver.current = null;
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!state} onOpenChange={(v) => !v && close(false)}>
        <AlertDialogContent className="sm:max-w-[420px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg">
              {state?.title}
            </AlertDialogTitle>
            {state?.body && (
              <AlertDialogDescription className="text-[13.5px] leading-relaxed">
                {state.body}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => close(false)}>
              {state?.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => close(true)}
              className={cn(
                state?.destructive
                  ? "bg-stock-out hover:bg-rose-700 text-white"
                  : "bg-brand hover:bg-brand-hover text-white"
              )}
            >
              {state?.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside ConfirmProvider");
  return ctx;
}
